import { createContext, useState, useCallback } from 'react';

import { Alerts } from '@nextroll/ar-style-base';

export interface IAlert {
    type: typeof Alerts.ALERT_TYPE_SUCCESS | typeof Alerts.ALERT_TYPE_ERROR;
    title?: string;
    message?: string;
    timestamp?: number;
}

export const NotificationContext = createContext({
    alerts: [] as IAlert[],
    addAlert: undefined as undefined | ((alert: IAlert) => void),
    dismissAlert: undefined as undefined | ((alert: IAlert) => void),
});

/**
 * Use 'addAlert' or 'dismissAlert' methods to display or dismiss alerts directly.
 * OR, this hook has already been embedded in the 'useRequest' hook, which can wrap
 * a REST request and handles loading state, error, and notification automatically.
 */
const useNotification = () => {
    const [alerts, setAlerts] = useState([] as IAlert[]);

    const addAlert = useCallback((alert: IAlert) => setAlerts(prevAlerts => [
        ...prevAlerts,
        {
            ...alert,
            isDismissable: true,
            timestamp: alert.timestamp || +new Date(),
            alertKey: `${alert.timestamp || +new Date()}`,
        } as IAlert,
    ]), []);
    const dismissAlert = useCallback(
        (alert: IAlert) => setAlerts(prevAlerts => prevAlerts.filter(a => a.timestamp !== alert.timestamp))
        , []);

    return [
        NotificationContext,
        alerts,
        addAlert,
        dismissAlert,
    ] as [
        typeof NotificationContext,
        IAlert[],
        undefined | ((alert: IAlert) => void),
        undefined | ((alert: IAlert) => void),
    ];
};

export default useNotification;
